import { orderBy } from "lodash";

export class Hierarchy<TKEY, T> {
  id: TKEY;
  name: string;
  origin: T;
  parent?: Hierarchy<TKEY, T>;

  parentId?: TKEY;
  children: Hierarchy<TKEY, T>[];
  flatParents: Hierarchy<TKEY, T>[];
  flatChildren: Hierarchy<TKEY, T>[];

  constructor(id: TKEY, name: string, origin: T, parent?: Hierarchy<TKEY, T>) {
    this.id = id;
    this.name = name;
    this.origin = origin;
    this.parent = parent;
    this.parentId = parent?.id;
    this.flatParents = parent ? [...parent.flatParents, parent] : [];
    this.children = [];
    this.flatChildren = [];
    parent?.addChild(this);
  }

  get level() {
    return this.flatParents.length;
  }

  orderBy(pred: (hierarchy: Hierarchy<TKEY, T>) => unknown) {
    this.children = orderBy(this.children, pred);
  }

  search(query: string): boolean {
    const normalizeQuery = query.trim().toLowerCase();
    const normalizeName = this.name.trim().toLowerCase();
    if (normalizeName.includes(normalizeQuery)) {
      return true;
    }
    return this.children.some((child) => child.search(query));
  }

  reverse() {
    this.children.forEach((child) => child.reverse());
    this.children = this.children.reverse();
  }

  includes(childOrId: Hierarchy<TKEY, T> | TKEY): boolean {
    if (childOrId instanceof Hierarchy) {
      return this.flatChildren.includes(childOrId);
    }
    return this.flatChildren.map((e) => e.id).includes(childOrId);
  }

  checkParent(parentOrId: Hierarchy<TKEY, T> | TKEY): boolean {
    if (parentOrId instanceof Hierarchy) {
      return this.flatParents.includes(parentOrId);
    }
    return this.flatParents.map((e) => e.id).includes(parentOrId);
  }

  addChild(child: Hierarchy<TKEY, T>) {
    if (!this.children.includes(child)) {
      this.children.push(child);
    }

    if (!this.flatChildren.includes(child)) {
      this.flatChildren.push(child);
    }

    this.flatParents.forEach((parent) => {
      if (!parent.flatChildren.includes(child)) {
        parent.flatChildren.push(child);
      }
    });
  }

  removeChild(child: Hierarchy<TKEY, T>) {
    child.children.forEach((e) => child.removeChild(e));

    if (this.children.includes(child)) {
      this.children = this.children.filter((e) => e !== child);
    }

    if (this.flatChildren.includes(child)) {
      this.flatChildren = this.flatChildren.filter((e) => e !== child);
    }

    this.flatParents.forEach((parent) => {
      if (parent.flatChildren.includes(child)) {
        parent.flatChildren = parent.flatChildren.filter((e) => e !== child);
      }
    });
  }

  /** All hierarchies that have no children */
  static ultimateHierarchies<TKEY, T>(hierarchies: Hierarchy<TKEY, T>[]) {
    return hierarchies.flatMap((hierarchy) => {
      if (hierarchy.children.length) {
        return hierarchy.flatChildren.filter((c) => c.children.length === 0);
      }
      return [hierarchy];
    });
  }

  static search<TKEY, T>(hierarchies: Hierarchy<TKEY, T>[], query: string) {
    const flatHierarchies = this.flat(hierarchies).filter((h) => h.search(query));
    const items = flatHierarchies.map((h) => h.origin);
    const getId = (item: T) => flatHierarchies.find((h) => h.origin === item)!.id;
    const getName = (item: T) => flatHierarchies.find((h) => h.origin === item)!.name;
    const getParentId = (item: T) => flatHierarchies.find((h) => h.origin === item)!.parentId;
    return this.from(items, getId, getId, getName, getParentId);
  }

  sortChildren() {
    this.children.sort((a, b) => (a.origin as any).order - (b.origin as any).order);
    this.children.forEach((child) => child.sortChildren());
  }

  static reverse<TKEY, T>(hierarchies: Hierarchy<TKEY, T>[]) {
    hierarchies.forEach((hierarchy) => hierarchy.reverse());
    return [...hierarchies].reverse();
  }

  static flat<TKEY, T>(hierarchies: Hierarchy<TKEY, T>[]) {
    return [...hierarchies, ...hierarchies.flatMap((h) => h.flatChildren)];
  }

  static from<TKEY, T>(
    items: T[],
    getId?: (item: T) => TKEY,
    getHierarchyId?: (item: T) => TKEY,
    getName?: (item: T) => string,
    getParentId?: (item: T) => TKEY | undefined,
    creator?: HierarchyCreator<TKEY, T>
  ) {
    const uniqItems = Array.from(new Set(items).values());
    let heep = [...uniqItems] as (T | null)[];
    const flatHierarchies: Hierarchy<TKEY, T>[] = [];

    while (heep.length !== 0) {
      heep = heep.filter(Boolean);
      [...heep].reduce((iterationResult, item, index) => {
        if (!getId) {
          getId = (it) => (it as Record<string, unknown>)["id"] as TKEY;
        }
        if (!getHierarchyId) {
          getHierarchyId = getId;
        }
        if (!getParentId) {
          getParentId = (it) => (it as Record<string, unknown>)["parent"] as TKEY;
        }
        if (!getName) {
          getName = (it) => (it as Record<string, unknown>)["name"] as string;
        }

        const id = item ? getId(item) : undefined;
        const parentId = item ? getParentId(item) : undefined;
        const hasParent = parentId && uniqItems.find((e) => getHierarchyId!(e) === parentId) !== undefined;
        const hierarchyParent = parentId && iterationResult.find((e) => getHierarchyId!(e.origin) === parentId);

        if (!hasParent || hierarchyParent) {
          heep[index] = null;
          const name = (item && getName(item)) || "";
          const hierarchy = creator
            ? creator(id!, name, item!, hierarchyParent)
            : new Hierarchy<TKEY, T>(id!, name, item!, hierarchyParent);
          iterationResult.push(hierarchy);
        }
        return iterationResult;
      }, flatHierarchies);
    }

    const result = flatHierarchies.filter((e) => !e.parent);
    result.forEach((hierarchy) => hierarchy.sortChildren());
    return result;
  }
}

export type HierarchyCreator<TKEY, T> = (id: TKEY, name: string, origin: T, parent?: Hierarchy<TKEY, T>) => Hierarchy<TKEY, T>;
